import React, {useEffect, useState} from 'react'
import {AspectRatio, GridProps, Link, SimpleGrid, Skeleton, useBreakpointValue, useMultiStyleConfig} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import type {ProductType} from '_types'
import {Picture} from '_uiComponents'
import {useAnalytics} from '_customHooks'
import {getProductAnalyticsInfo, getProductImpressionAnalytics} from '_analytics/utils/analyticsUtils'
import {updateCloudinarySize} from '_utils'

interface SearchProductsGridProps {
    products?: Array<ProductType>
    isLoading: boolean
    onProductClick?: () => void
}

/**
 * @name SearchProductsGrid
 *
 * @component
 *
 * @description
 * Renders a grid containing a list of images representing products.
 *
 * @remarks
 * Analytics is handled after client render because number of shown products can be different based on viewport.
 *
 * @param {SearchProductsGridProps} props - Properties object.
 * @param {Array<ProductType>} [props.products] - List of products.
 * @param {boolean} props.isLoading - Loading status.
 * @param {() => void} props.onProductClick - Callback for product click event.
 *
 * @returns {React.FC<SearchProductsGridProps>} Rendered SearchProductsGrid.
 */
const SearchProductsGrid: React.FC<SearchProductsGridProps> = ({products, isLoading, onProductClick}) => {
    const styles = useMultiStyleConfig('SearchSection') as Record<string, GridProps>
    const breakpoint = useBreakpointValue({base: 'base', lg: 'lg', xl: 'xl'})
    const [isClient, setIsClient] = useState(false)
    const {analyticsProductImpression, analyticsProductClick} = useAnalytics()

    const handleProductClick = (product: ProductType, position: number) => {
        const {productCurrency} = getProductAnalyticsInfo(product)
        const analyticsProductObject = getProductImpressionAnalytics({
            product,
            idx: position,
            analyticsList: 'search-suggested',
        })

        analyticsProductClick({
            currencyCode: productCurrency,
            actionField: {
                list: 'search-suggested',
            },
            product: analyticsProductObject,
        })

        onProductClick?.()
    }

    /**
     * Triggers an `analyticsProductImpression` event for each shown product.
     * Analytics business logic for product impressions is handled on client-side
     * since visible products count is different on each viewport.
     * @param {number} visibleProductsCount
     */
    const handleProductImpressionsAnalytics = (visibleProductsCount: number) => {
        products?.slice(0, visibleProductsCount).forEach((product, idx) => {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({
                product,
                idx,
                analyticsList: 'search-suggested',
            })

            analyticsProductImpression({
                currencyCode: productCurrency,
                impressions: analyticsProductObject,
            })
        })
    }

    useEffect(() => {
        if (isClient && products) {
            const visibleProductsCount = breakpoint === 'base' ? 6 : breakpoint === 'lg' ? 4 : 8
            handleProductImpressionsAnalytics(visibleProductsCount)
        }
    }, [isClient, products])

    useEffect(() => {
        setIsClient(true)
    }, [])

    return (
        <SimpleGrid data-testid="search-products-grid" {...styles.productsGrid}>
            {isLoading ? (
                <ProductSkeletons />
            ) : (
                products?.map((product, index) => {
                    const {activeVariation} = getProductAnalyticsInfo(product)
                    // For sets, activeVariation is the flat `c_setData` (images + pdpUrl on it);
                    // for variation groups it is the active variation. Both expose `images`/`pdpUrl`.
                    const variationImage = activeVariation?.images || activeVariation?.images?.large?.[0]
                    const productImageSrc = variationImage?.url ?? ''
                    const productImageAlt = variationImage?.alt ?? ''
                    const productPdpUrl = activeVariation?.pdpUrl ?? ''

                    return (
                        <Link
                            as={RouteLink}
                            to={productPdpUrl}
                            key={`${product.id}-${index}`}
                            onClick={() => handleProductClick(product, index)}
                            display={{
                                base: index < 6 ? 'block' : 'none',
                                lg: index < 4 ? 'block' : 'none',
                                xl: 'block',
                            }}
                            data-testid={`search-product-${product.id}`}
                        >
                            <AspectRatio ratio={90 / 115}>
                                <Picture
                                    imagexs={updateCloudinarySize(productImageSrc, 400)}
                                    imagesm={updateCloudinarySize(productImageSrc, 400)}
                                    imagemd={updateCloudinarySize(productImageSrc, 400)}
                                    imagelg={updateCloudinarySize(productImageSrc, 250)}
                                    imagexl={updateCloudinarySize(productImageSrc, 150)}
                                    alt={productImageAlt}
                                    title={product.name}
                                    hasLazy={false}
                                />
                            </AspectRatio>
                        </Link>
                    )
                })
            )}
        </SimpleGrid>
    )
}

/**
 * @name ProductSkeletons
 *
 * @component
 *
 * @description
 * Renders a list of skeletons representing images loading.
 *
 * @returns {JSX.Element} Rendered skeletons.
 */
const ProductSkeletons = (): JSX.Element => {
    const styles = useMultiStyleConfig('SearchSection') as Record<string, GridProps>
    const maxProductsCount = 8

    return (
        <>
            {Array.from({length: maxProductsCount}, (_, index) => (
                <AspectRatio
                    ratio={90 / 115}
                    key={`product-skeleton-${index}`}
                    display={{
                        base: index < 6 ? 'block' : 'none',
                        lg: index < 4 ? 'block' : 'none',
                        xl: 'block',
                    }}
                >
                    <Skeleton data-testid={`product-skeleton-${index}`} {...styles.productSkeleton} />
                </AspectRatio>
            ))}
        </>
    )
}

export default SearchProductsGrid
